import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, MapPin, Search } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { PageIntro, Reveal, Shell } from "@/components/layout/Shell";

const initials = name => (name || "D").split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();

export default function PublicPage({ type, title, description, canSearch = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api(`/public/${type}?search=${encodeURIComponent(search)}`)
      .then(d => setItems(d.items || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [type, search]);

  return (
    <Shell>
      <main className="inner-page">
        <PageIntro crumb={`DCEA / ${title}`} title={title} description={description}>
          {canSearch && (
            <div className="search-box">
              <Search size={17} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${type}…`}
                data-testid={`${type}-search-input`}
              />
            </div>
          )}
        </PageIntro>
        <div className="container page-grid">
          {loading ? (
            <div className="empty-state" data-testid={`${type}-loading`}>Loading…</div>
          ) : error ? (
            <div className="empty-state" data-testid={`${type}-error`}>Could not load {title.toLowerCase()} — {error}</div>
          ) : items.length ? (
            items.map((item, i) => {
              const image = item.image || item.cover || (type === "members" && item.picture ? mediaUrl(item.picture) : null);
              return (
                <Reveal key={item.id || i} delay={(i % 3) * 0.08}>
                  <article className="listing-card" data-testid={`${type}-item-${i}`}>
                    {image ? (
                      <img src={image} alt={item.title || item.name} />
                    ) : (
                      <div className="monogram"><b>{initials(item.name || item.title)}</b></div>
                    )}
                    <div>
                      <span className="card-kicker">{item.category || item.position || item.designation || item.membership_type || item.status || "DCEA"}</span>
                      <h2>{item.title || item.name}</h2>
                      <p>{item.description || item.excerpt || item.bio || item.organization || "Discover more from the DCEA community."}</p>
                      {type === "members" && item.bio && item.organization && <small>{item.organization}</small>}
                      {(item.date || item.city) && <small>{item.city && <MapPin size={11} />}{item.date || item.city}</small>}
                      {type === "members" && (item.email || item.phone) && <small>{[item.email, item.phone].filter(Boolean).join(" · ")}</small>}
                      {type === "members" && item.linkedin && <small><a href={item.linkedin} target="_blank" rel="noreferrer" className="text-link" style={{ marginTop: 0 }} data-testid={`member-linkedin-${i}`}><Linkedin size={12} /> LinkedIn</a></small>}
                      {type === "news" && <small><Link to={`/news/${item.id}`} className="text-link" style={{ marginTop: 0 }} data-testid={`news-read-${i}`}>Read more <ArrowRight size={12} /></Link></small>}
                    </div>
                  </article>
                </Reveal>
              );
            })
          ) : (
            <div className="empty-state" data-testid={`${type}-empty-state`}>No {title.toLowerCase()} available right now.</div>
          )}
        </div>
      </main>
    </Shell>
  );
}
